/**
 * A UAE Arabic dayjs locale with Latin digits.
 *
 * ## Why this file exists rather than `import 'dayjs/locale/ar'`
 *
 * dayjs's bundled `ar` locale defines `preparse` and `postformat` hooks that rewrite every digit
 * to Arabic-Indic form (`٠١٢٣`) on the way out of `.format()` and back on the way in. There are
 * 300+ `.format()` calls in `src/`, so importing the stock locale would silently undo the
 * `-u-nu-latn` pinning that `format.ts` establishes — and it would do so only under Arabic, which
 * is exactly the kind of defect that reaches production.
 *
 * `preparse` is the more dangerous half: it rewrites digits on *parse*, so a value round-tripping
 * through dayjs could reach an API in Arabic-Indic form.
 *
 * So this registers `ar-ae` with the Arabic month and day names — which are what actually matter —
 * and no digit rewriting at all.
 *
 * Month names are the MSA forms used across the Gulf (كانون/شباط are the Levantine alternatives
 * and are deliberately not used here). Weekday order starts at Sunday because that is dayjs's
 * index-0 convention; `weekStart` below is what actually controls calendar rendering.
 *
 * Design: docs/43-I18N-ARABIC-DESIGN.md §6.
 */

import dayjs from 'dayjs';

const months = [
  'يناير',
  'فبراير',
  'مارس',
  'أبريل',
  'مايو',
  'يونيو',
  'يوليو',
  'أغسطس',
  'سبتمبر',
  'أكتوبر',
  'نوفمبر',
  'ديسمبر',
];

const weekdays = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

const arAe = {
  name: 'ar-ae',
  months,
  monthsShort: months,
  weekdays,
  weekdaysShort: weekdays,
  weekdaysMin: ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س'],
  /**
   * Monday. The UAE moved to a Monday–Friday working week in January 2022, so a calendar starting
   * on Sunday misrepresents the working week for every UAE tenant.
   */
  weekStart: 1,
  // NOTE: no `preparse` / `postformat`. See the module header — their absence is the point.
  formats: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'DD/MM/YYYY',
    LL: 'D MMMM YYYY',
    LLL: 'D MMMM YYYY HH:mm',
    LLLL: 'dddd D MMMM YYYY HH:mm',
  },
  relativeTime: {
    future: 'خلال %s',
    past: 'منذ %s',
    s: 'ثوانٍ',
    m: 'دقيقة',
    mm: '%d دقائق',
    h: 'ساعة',
    hh: '%d ساعات',
    d: 'يوم',
    dd: '%d أيام',
    M: 'شهر',
    MM: '%d أشهر',
    y: 'عام',
    yy: '%d أعوام',
  },
  ordinal: (n: number) => `${n}`,
};

let registered = false;

/** Registers `ar-ae` with dayjs. Idempotent — safe to call on every locale change. */
export const registerArabicDayjsLocale = (): void => {
  if (registered) return;
  dayjs.locale(arAe as never, undefined, true);
  registered = true;
};

export default arAe;
